import {
  UPLOAD_IMAGE_MIN_CROP_SIZE,
  UPLOAD_IMAGE_OUTPUT_TYPE,
} from '../../UploadImage.constants'
import { clamp, clampCropPosition } from '../../UploadImage.utils'

function isQuarterTurn(rotation) {
  return rotation === 90 || rotation === 270
}

// Lays out the image "contain"-fit inside the stage, accounting for rotation.
// `display*` describe the rotated bounding box (used for crop bounds); the
// `element*` fields size/position the unrotated <img> that is then CSS-rotated.
export function getImageLayout({
  naturalHeight,
  naturalWidth,
  rotation,
  stageHeight,
  stageWidth,
}) {
  const rotatedWidth = isQuarterTurn(rotation) ? naturalHeight : naturalWidth
  const rotatedHeight = isQuarterTurn(rotation) ? naturalWidth : naturalHeight

  const scale = Math.min(stageWidth / rotatedWidth, stageHeight / rotatedHeight)

  const displayWidth = rotatedWidth * scale
  const displayHeight = rotatedHeight * scale

  const elementWidth = naturalWidth * scale
  const elementHeight = naturalHeight * scale

  return {
    displayHeight,
    displayWidth,
    elementHeight,
    elementLeft: (stageWidth - elementWidth) / 2,
    elementTop: (stageHeight - elementHeight) / 2,
    elementWidth,
    imageX: (stageWidth - displayWidth) / 2,
    imageY: (stageHeight - displayHeight) / 2,
    scale,
  }
}

// Largest centered crop with the given aspect that fits the displayed image.
export function getInitialCrop({ aspect, layout }) {
  const { displayHeight, displayWidth, imageX, imageY } = layout

  const isWiderThanAspect = displayWidth / displayHeight > aspect
  const height = isWiderThanAspect ? displayHeight : displayWidth / aspect
  const width = isWiderThanAspect ? displayHeight * aspect : displayWidth

  return {
    height,
    width,
    x: imageX + (displayWidth - width) / 2,
    y: imageY + (displayHeight - height) / 2,
  }
}

// Preserves the previous crop's size after a rotation, clamped to the rotated
// bounds, instead of resetting to the largest centered crop.
export function getRotatedCrop({ aspect, layout, previousCrop }) {
  const maxWidth = Math.min(layout.displayWidth, layout.displayHeight * aspect)
  const width = clamp(previousCrop.width, UPLOAD_IMAGE_MIN_CROP_SIZE, maxWidth)
  const height = width / aspect

  return clampCropPosition({
    crop: { ...previousCrop, height, width },
    layout,
  })
}

// Re-fits an existing crop after the stage was resized (same image): scales it
// by the fit ratio so it stays over the same region of the image.
export function getRefitCrop({ aspect, nextLayout, previousCrop, prevLayout }) {
  if (!previousCrop) {
    return getInitialCrop({ aspect, layout: nextLayout })
  }

  const ratio = nextLayout.scale / prevLayout.scale

  return clampCropPosition({
    crop: {
      height: previousCrop.height * ratio,
      width: previousCrop.width * ratio,
      x: nextLayout.imageX + (previousCrop.x - prevLayout.imageX) * ratio,
      y: nextLayout.imageY + (previousCrop.y - prevLayout.imageY) * ratio,
    },
    layout: nextLayout,
  })
}

// Renders the rotated image to an offscreen canvas, crops the selected region
// at native resolution, and returns the result as a Blob + data URL.
export function renderCroppedImage({
  crop,
  cropShape,
  image,
  layout,
  naturalHeight,
  naturalWidth,
  rotation,
}) {
  const sourceX = (crop.x - layout.imageX) / layout.scale
  const sourceY = (crop.y - layout.imageY) / layout.scale
  const sourceWidth = crop.width / layout.scale
  const sourceHeight = crop.height / layout.scale

  const rotatedWidth = isQuarterTurn(rotation) ? naturalHeight : naturalWidth
  const rotatedHeight = isQuarterTurn(rotation) ? naturalWidth : naturalHeight

  const rotatedCanvas = document.createElement('canvas')

  rotatedCanvas.width = rotatedWidth
  rotatedCanvas.height = rotatedHeight

  const rotatedContext = rotatedCanvas.getContext('2d')

  rotatedContext.translate(rotatedWidth / 2, rotatedHeight / 2)
  rotatedContext.rotate((-rotation * Math.PI) / 180)
  rotatedContext.drawImage(image, -naturalWidth / 2, -naturalHeight / 2)

  const outputWidth = Math.round(sourceWidth)
  const outputHeight = Math.round(sourceHeight)

  const canvas = document.createElement('canvas')

  canvas.width = outputWidth
  canvas.height = outputHeight

  const context = canvas.getContext('2d')

  context.drawImage(
    rotatedCanvas,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    outputWidth,
    outputHeight
  )

  const dataUrl = canvas.toDataURL(UPLOAD_IMAGE_OUTPUT_TYPE)

  return new Promise(resolve => {
    canvas.toBlob(blob => {
      resolve({
        blob,
        cropShape,
        dataUrl,
        height: outputHeight,
        width: outputWidth,
      })
    }, UPLOAD_IMAGE_OUTPUT_TYPE)
  })
}
